import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { Holiday, HolidayBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { errorMessage, holidaysOptions } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
// formatBRDate parseia 'YYYY-MM-DD' em local time (não desloca o dia) e
// formata dd/mm/aaaa — mesmo helper da Agenda.
import { formatBRDate } from '@/features/agenda/agenda-format';
import {
  matchesText,
  normalizeText,
  SettingsBreadcrumb,
  SettingsSearch,
} from '@/features/settings/settings-ui';

/**
 * /settings/desks/holidays — página de feriados (antes um dialog): tabela
 * Data | Nome | ações e "+ Feriado". Feriados valem para todas as mesas e
 * pausam a contagem de SLA em horário útil.
 */
export function HolidaysSettingsPage() {
  const queryClient = useQueryClient();
  const holidays = useQuery(holidaysOptions());
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ holiday: Holiday | null } | null>(null);

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/desks/holidays/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks', 'holidays'] }),
  });

  const q = normalizeText(query.trim());
  const list = [...(holidays.data ?? [])]
    .sort((a, b) => a.date.localeCompare(b.date))
    .filter((h) =>
      q === '' ? true : matchesText(h.name, q) || matchesText(formatBRDate(h.date), q)
    );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb page="Feriados" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Feriados</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar feriado" />
          <Button size="sm" className="h-9" onClick={() => setDialog({ holiday: null })}>
            <Plus className="size-4" strokeWidth={2.5} />
            Feriado
          </Button>
        </div>
      </div>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Feriados valem para todas as mesas e pausam a contagem de SLA em horário útil.
      </p>

      <div className="mt-4">
        {holidays.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : holidays.isError ? (
          <EmptyState message={`Erro ao carregar feriados: ${errorMessage(holidays.error)}`} />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhum feriado encontrado' : 'Nenhum feriado cadastrado'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 w-40 px-4 font-semibold">Data</th>
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 w-24 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((h) => (
                  <tr
                    key={h.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="px-4 font-medium tabular-nums whitespace-nowrap">
                      {formatBRDate(h.date)}
                    </td>
                    <td className="max-w-96 truncate px-4">{h.name}</td>
                    <td className="px-4">
                      <div className="flex items-center justify-end gap-0.5">
                        <DeskRowAction
                          label="Editar feriado"
                          onClick={() => setDialog({ holiday: h })}
                        >
                          <Pencil className="size-3.5" />
                        </DeskRowAction>
                        <DeskRowAction
                          label="Excluir feriado"
                          destructive
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Excluir o feriado "${h.name}"?`)) {
                              remove.mutate(h.id);
                            }
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </DeskRowAction>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {remove.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}

      {dialog ? <HolidayDialog holiday={dialog.holiday} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}

function HolidayDialog({ holiday, onClose }: { holiday: Holiday | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(holiday?.date ?? '');
  const [name, setName] = useState(holiday?.name ?? '');

  const save = useMutation({
    mutationFn: async (body: HolidayBody) => {
      const res = holiday
        ? await api<Holiday>(`/desks/holidays/${holiday.id}`, { method: 'PUT', body })
        : await api<Holiday>('/desks/holidays', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks', 'holidays'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!date || !name.trim()) return;
    save.mutate({ date, name: name.trim() });
  };

  return (
    <Dialog open onClose={onClose} title={holiday ? 'Editar feriado' : 'Novo feriado'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="holiday-date">
            Data <span className="text-destructive">*</span>
          </Label>
          <Input
            id="holiday-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            autoFocus
            className="w-44"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="holiday-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="holiday-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Natal"
            required
          />
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !date || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
